import {
  ADDRESS_INPUTS_MAP,
  getAddnConfigInput,
  getEducationNameField,
  MARKSHEET_INPUT
} from '@config/addn-configs';
import { BGVInsuffPayload } from '@containers/form-verification/insufficiency/types';
import { CLIENT_FLOWS } from '@global/constants/general';
import { TxnState } from '@global/constants/verification';
import {
  base64ToBinary,
  evaluateParams,
  getAccessToken,
  isAddressBased
} from '@global/helpers/general';
import { TxnInfo, TxnVerification, VerificationConfig } from '@global/types/form';
import { updateBehaviorCustomBehaviour } from '@redux/slices/behavior';
import { updateFormData } from '@redux/slices/form-data';
import { updateProfileData } from '@redux/slices/profile';
import { updateVerificationConfigs } from '@redux/slices/verification';
import { store } from '@redux/store';
import { AxiosInstance } from 'axios';
import { axiosInstances, getAxiosHeaders } from './config/axios';
import { customApiEndpoints, routes } from './config/routes';

export const getOrg = async (orgId: string) => {
  try {
    const res = await axiosInstances.verificationV1?.get(`${routes.verifier.base}/${orgId}`);
    return res?.data;
  } catch (error) {
    console.info(error);
  }
};

export const createTransaction = async (reqData: { orgId: string, eKycFormId: string }) => {
  try {
    const res = await axiosInstances.verificationV1?.post(
      `${routes.transactions.base}`,
      reqData,
      getAxiosHeaders()
    );
    return res?.data;
  } catch (error) {
    console.info(error);
  }
};

export const processTransaction = async (reqData: {
  orgId: string,
  txnId: string,
  [x: string]: any
}) => {
  try {
    const res = await axiosInstances.verificationV1?.post(
      `${routes.transactions.process}`,
      reqData,
      getAxiosHeaders()
    );
    return res?.data;
  } catch (error) {
    console.info(error);
  }
};

export const processEvaluationTransaction = async (reqData: {
  orgId: string,
  txnId: string,
  [x: string]: any
}) => {
  // return true;
  try {
    const res = await axiosInstances.verificationV1?.post(
      `${routes.transactions.processEvaluation}`,
      reqData,
      getAxiosHeaders()
    );
    return res?.data;
  } catch (error) {
    console.info(error);
    return false;
  }
};

export const getProfile = async (token?: string) => {
  try {
    // console.info(
    //   'flag::getProfile::',
    //   getAxiosHeaders(token ? { Authorization: `Bearer ${token}` } : {})
    // );
    const res = await axiosInstances.webWallet?.get(
      routes.profile.base,
      getAxiosHeaders(token ? { Authorization: `Bearer ${token}` } : {})
    );
    const formattedData = structuredClone(res?.data?.data);
    // change 'profession' to 'employment' in profile data
    if (formattedData?.verificationDetailList?.profession) {
      formattedData.verificationDetailList.employment =
        formattedData?.verificationDetailList?.profession;
      delete formattedData?.verificationDetailList?.profession;
    }
    if (res?.data?.code === 0) {
      res.data.data = formattedData;
      store.dispatch(updateProfileData(res?.data?.data));
    }
    return res?.data;
  } catch (error) {
    console.info(error);
  }
};

const getAddressReqdConfig = (configData: VerificationConfig) => {
  let arr: unknown[] = [];
  if (configData?.additionalInfo?.authOptions?.[0]?.showSpecificForm?.length > 0) {
    configData?.additionalInfo?.authOptions?.[0]?.showSpecificForm?.forEach((row) => {
      if (ADDRESS_INPUTS_MAP[row?.name]) {
        arr = [...arr, ...ADDRESS_INPUTS_MAP[row?.name]];
      }
    });
  }
  return arr;
};

const checkVisibility = (args: any, fn: any = null) => {
  if (!fn) {
    return true;
  } else if (typeof fn === 'object') {
    return !fn?.hidden?.includes('web-wallet');
  } else {
    fn = eval(fn);
    const v = fn(args);
    return v;
  }
};

export const getConfigs = async (
  data: string[],
  marksheetRequired: { [key: string]: boolean } = {},
  formInfo: { [key: string]: TxnVerification } = {}
) => {
  try {
    const res = await axiosInstances.verificationWebWallet?.post(
      `${routes.verificationConfigs.info}`,
      {
        verificationNameList: data
      },
      {
        headers: { Authorization: `Bearer ${getAccessToken()}` }
      }
    );
    if (res?.data?.code === 0) {
      const formattedRes = res;
      formattedRes.data.data = (formattedRes?.data?.data ?? [])?.reduce((acc: any, cur: any) => {
        return {
          ...acc,
          [cur?.name]: {
            ...cur,
            metadata: {
              ...cur?.metadata,
              label:
                cur?.metadata?.orgSpecificName?.[import.meta.env.VITE_ORG] ?? cur?.metadata?.label
            },
            additionalInfo: {
              ...cur?.additionalInfo,
              name:
                cur?.metadata?.orgSpecificName?.[import.meta.env.VITE_ORG] ?? cur?.metadata?.label,
              authOptions: cur?.additionalInfo?.authOptions?.filter((option: any) => {
                if (option?.hidden && option?.hidden?.['web-wallet']) {
                  return false;
                } else {
                  return true;
                }
              })
            }
          }
        };
      }, {});
      const formattedConfigs = formattedRes?.data?.data;

      for (const key of Object.keys(formattedConfigs ?? {})) {
        const name = key;
        const config = formattedConfigs?.[key];
        const authOptions = config?.additionalInfo?.authOptions;
        if (
          authOptions?.length > 0 &&
          authOptions?.[0]?.page &&
          authOptions?.[0]?.page?.config?.[0]?.inputs
        ) {
          authOptions[0].page.config[0].inputs = authOptions[0].page.config[0].inputs?.filter(
            (r: { [key: string]: string }) => checkVisibility(formInfo?.[key], r?.visibility)
          );

          if (
            config.category.name === 'Education' &&
            import.meta.env.VITE_MAKE_INSTITUTE_NAME_TEXT_FIELD === 'true'
          ) {
            authOptions[0].page.config[0].inputs = authOptions[0]?.page?.config?.[0]?.inputs?.map(
              (r: { [key: string]: string }) => {
                if (['nameOfInstitute', 'nameOfBoard']?.includes(r?.fieldName)) {
                  r.inputType = 'field';
                } else if (['nameOfProgram']?.includes(r?.fieldName)) {
                  r.inputType = 'field';
                  r.helperText = 'Enter the program from your selected Degree';
                }
                return r;
              }
            );
          }
        }
        const readOnlyProfile =
          config.additionalInfo.authOptions[0]?.page?.config?.[0]?.readOnly?.profile;
        if (config?.additionalInfo?.authOptions?.[0]?.page) {
          if (readOnlyProfile) {
            let readOnlyInputs: any[] = [];
            for (const key of Object.keys(readOnlyProfile)) {
              const field = getAddnConfigInput(readOnlyProfile[key], key, true, 'field', true);
              readOnlyInputs = [...readOnlyInputs, field];
            }
            config.additionalInfo.authOptions[0].page.config[0] = {
              ...config.additionalInfo.authOptions[0].page.config[0],
              inputs: [
                ...config.additionalInfo.authOptions[0].page.config[0].inputs,
                ...readOnlyInputs
              ]
            };
          }
          if (marksheetRequired[name]) {
            const degreeIdx = config.additionalInfo.authOptions[0].page.config[0].inputs.findIndex(
              (input: any) => input.fieldName === 'Degree'
            );
            const updatedInputs = config.additionalInfo.authOptions[0].page.config[0].inputs;
            updatedInputs.splice(degreeIdx + 1, 0, MARKSHEET_INPUT);
            config.additionalInfo.authOptions[0].page.config[0] = {
              ...config.additionalInfo.authOptions[0].page.config[0],
              inputs: [...updatedInputs]
            };
          }
          if (isAddressBased(config)) {
            const initialInputs = config.additionalInfo.authOptions[0].page.config[0]?.inputs ?? [];
            const inputs = [...initialInputs, ...getAddressReqdConfig(config)];
            config.additionalInfo.authOptions[0].page.config[0] = {
              ...config.additionalInfo.authOptions[0].page.config[0],
              inputs
            };
          } else if (
            config?.additionalInfo?.authOptions?.[0]?.showSpecificForm?.[0]?.name?.includes(
              'nameAsPer'
            )
          ) {
            const filteredInputs =
              config.additionalInfo.authOptions[0].page.config[0].inputs?.filter(
                (r: any) =>
                  r?.fieldName !==
                  config?.additionalInfo?.authOptions?.[0]?.showSpecificForm?.[0]?.name
              ) ?? [];
            config.additionalInfo.authOptions[0].page.config[0].inputs = [
              getEducationNameField(
                config?.additionalInfo?.authOptions?.[0]?.showSpecificForm?.[0]?.name,
                config?.additionalInfo?.authOptions?.[0]?.showSpecificForm?.[0]?.label
              ),
              ...filteredInputs
            ];
          }
        }
      }

      store.dispatch(updateVerificationConfigs(formattedConfigs));
      return formattedRes?.data;
    }
    return res?.data;
  } catch (e) {
    console.info(e);
  }
};

export const getTxnInfo = async (txnId: string, orgId: string) => {
  try {
    const res = await axiosInstances.verificationV1?.get(
      `${routes.transactions.getById(txnId, orgId)}`,
      getAxiosHeaders()
    );
    if (res?.data?.code === 0) {
      const formattedResponse = res;
      const currentTime = new Date().getTime();
      if (
        !import.meta.env.VITE_ENABLE_EVALUATION &&
        import.meta.env.VITE_CLIENT_FLOW !== CLIENT_FLOWS.CM
      ) {
        delete formattedResponse?.data?.data?.evaluation;
        delete formattedResponse?.data?.data?.evaluationInfo;
      } else if (formattedResponse?.data?.data?.evaluation) {
        store.dispatch(
          updateBehaviorCustomBehaviour({
            skipEvaluationFlow: formattedResponse?.data?.data?.evaluation?.skipEvaluationFlow
          })
        );
      }
      const expiryTime = formattedResponse?.data?.data?.form?.expiry;
      formattedResponse.data.data.form.isExpired =
        expiryTime !== -1 && currentTime > +expiryTime * 1000;
      formattedResponse.data.data.filtered = {
        done:
          res?.data?.data?.verificationList
            ?.filter((r: any) => r?.state === TxnState.COMPLETED)
            ?.map((r: any) => r?.name) ?? [],
        remaining:
          res?.data?.data?.verificationList
            ?.filter((r: any) => r?.state === TxnState.NOT_INITIALIZED && r?.markedAsSkip === false)
            ?.map((r: any) => r?.name) ?? []
      };
      formattedResponse.data.data.verificationList = (
        res?.data?.data?.verificationList ?? []
      )?.reduce((acc: any, cur: any) => {
        return {
          ...acc,
          [cur?.name]: {
            ...(cur ?? {}),
            proceeded: Boolean(cur?.markedAsSkip || cur?.state !== TxnState.NOT_INITIALIZED)
          }
        };
      }, {});
      if (
        import.meta.env.VITE_ENABLE_EVALUATION &&
        import.meta.env.VITE_CLIENT_FLOW !== CLIENT_FLOWS.CM &&
        formattedResponse?.data?.data?.state === TxnState.NOT_INITIALIZED
      ) {
        await processEvaluationTransaction({
          orgId,
          txnId,
          evalData: [
            {
              evaluationProvider: 'trential',
              evaluationServiceType: 'evaluation'
            }
          ]
        });
      }
      store.dispatch(
        updateFormData({
          txnId,
          txnInfo: {
            ...formattedResponse?.data?.data
          }
        })
      );
      return formattedResponse?.data;
    }
    return res?.data;
  } catch (e) {
    console.info(e);
  }
};

export const fileUpload = async (file: File, fileCategory?: string, folderName?: string) => {
  const formData = new FormData();
  formData.append('file', file);
  let url = '';
  if (folderName && !fileCategory) {
    url = `${routes.files.base}?folderName=${folderName}`;
  } else if (!folderName && fileCategory) {
    url = `${routes.files.base}?fileCategory=${fileCategory}`;
  } else {
    url = `${routes.files.base}`;
  }
  try {
    const res = await axiosInstances.utility?.post(url, formData, {
      headers: {
        Authorization: `Bearer ${getAccessToken()}`
      }
    });
    return res?.data;
  } catch (error) {
    console.info(error);
  }
};

export const fileUploadFromUrl = async (url: string, folderName?: string) => {
  try {
    const res = await axiosInstances.utility?.post(
      `${routes.files.fromUrl}${folderName ? `?folderName=${folderName}` : ''}`,
      { url },
      {
        headers: {
          Authorization: `Bearer ${getAccessToken()}`
        }
      }
    );
    return res?.data;
  } catch (error) {
    console.info(error);
  }
};

export const updateProfile = async (reqData: {
  professionList: string[],
  fatherName: string,
  educationLevel: string
}) => {
  try {
    const res = await axiosInstances.webWallet?.patch(
      `${routes.profile.base}`,
      reqData,
      getAxiosHeaders()
    );
    return res?.data;
  } catch (error) {
    console.info(error);
  }
};

export const makeApiCall = async (
  url: string,
  method: string,
  payload: unknown,
  axiosInstance: AxiosInstance | undefined = axiosInstances.default
) => {
  try {
    const res = await axiosInstance?.({
      method,
      url,
      data: payload,
      ...getAxiosHeaders()
    });
    return res?.data;
  } catch (error) {
    console.info(error);
  }
};

export const searchAutocompleteAPITrigger = async (
  searchThis = '',
  newSeedValue = 0,
  input: {
    fetcher: {
      api: { body: any, endpoint: string, params: string, endpointBaseUrl: string, method: string },
      method: string
    }
  },
  fields: any[],
  verificationName: string
) => {
  try {
    const { endpointBaseUrl, endpoint, method, params } = input?.fetcher?.api ?? {};

    // Resolve base endpoint and axios instance
    let apiEndpoint = endpoint;
    let axiosInstance: AxiosInstance | undefined = axiosInstances.default;
    let axiosInstanceMethod: string | undefined = method;

    if (endpointBaseUrl) {
      const {
        url,
        axiosInstance: instance,
        method: instanceMethodFromCustomApiEndpoints
      } = customApiEndpoints[endpointBaseUrl as string](verificationName);

      apiEndpoint = url;
      axiosInstance = instance;
      axiosInstanceMethod = instanceMethodFromCustomApiEndpoints || method;
    }

    // Build query params including dynamic params
    let queryParams: Record<string, any> = {
      seed: newSeedValue,
      count: 50,
      sort: 'name',
      order: 1,
      search: searchThis
    };

    if (params) {
      const evaluatedParams = evaluateParams(params, fields[0], {});
      queryParams = { ...queryParams, ...evaluatedParams };
    }
    const urlWithParams = `${apiEndpoint}?${new URLSearchParams(
      queryParams as Record<string, string>
    ).toString()}`;

    const response = await axiosInstance?.({
      method: (axiosInstanceMethod || input?.fetcher?.method || 'GET').toUpperCase(),
      url: urlWithParams,
      ...getAxiosHeaders(),
      data: {}
    });
    let bodyForPatch = eval(input?.fetcher?.api?.body);
    bodyForPatch = bodyForPatch(response);

    return { ...bodyForPatch };
  } catch {
    return { list: [{ name: 'Other', value: 'Other' }], total: 0 };
  }
};

export const saveVerificationInProfile = async (reqData: unknown) => {
  try {
    const res = await axiosInstances.webWallet?.put(
      `${routes.profile.verificationDetail}`,
      reqData,
      getAxiosHeaders()
    );
    return res?.data;
  } catch (error) {
    console.info(error);
  }
};

export const getVerificationByTxnId = async (
  txnId: string,
  orgId: string,
  verificationName: string
) => {
  try {
    const res = await axiosInstances.verificationV1?.get(
      `${routes.transactions.getVerificationById(txnId, orgId, verificationName)}`,
      getAxiosHeaders()
    );
    return res?.data;
  } catch (e) {
    console.info(e);
  }
};

export const getCaptcha = async (verificationName: string, payload: any) => {
  try {
    const res = await axiosInstances.verificationWebWallet?.post(
      `${routes.verifications.captcha(verificationName)}`,
      payload,
      getAxiosHeaders()
    );
    return res?.data;
  } catch (e) {
    console.info(e);
  }
};

export const uploadCredentialDoc = async (image: string, type: string) => {
  const formData = new FormData();
  const imageInBinary = await base64ToBinary(image);
  formData.append('file', imageInBinary);
  try {
    const res = await axiosInstances.webWallet?.post(
      routes.credentials.documents(type),
      formData,
      getAxiosHeaders({
        'Content-Type': 'multipart/form-data'
      })
    );
    return res?.data;
  } catch (error) {
    console.info(error);
  }
};

export const deleteVerificationFromProfile = async (verificationName: string, subType: string) => {
  try {
    const res = await axiosInstances.webWallet?.delete(`${routes.profile.verificationDetail}`, {
      data: { verificationName, subType },
      ...getAxiosHeaders()
    });
    return res?.data;
  } catch (e) {
    console.info(e);
  }
};

export const deleteBGV = async (verificationName: string, orgId: string, transactionId: string) => {
  try {
    const res = await axiosInstances.verificationV1?.delete(`${routes.bgv.base}`, {
      data: { verificationName, orgId, transactionId },
      ...getAxiosHeaders()
    });
    return res?.data;
  } catch (e) {
    console.info(e);
  }
};

export const postMultiDocList = async (reqData: {
  txnId: string,
  orgId: string,
  verificationName: string,
  data: unknown
}) => {
  try {
    const res = await axiosInstances.verificationV2?.post(
      routes.bgv.base,
      reqData,
      getAxiosHeaders()
    );
    return res?.data;
  } catch (error) {
    console.info(error);
  }
};

export const updateBGV = async (reqData: {
  txnId: string,
  orgId: string,
  data: unknown,
  subType: string
}) => {
  try {
    const res = await axiosInstances.verificationV2?.put(
      routes.bgv.base,
      reqData,
      getAxiosHeaders()
    );
    return res?.data;
  } catch (error) {
    console.info(error);
  }
};

export const getCredentials = async (txnInfo?: TxnInfo) => {
  try {
    const res = await axiosInstances.webWallet?.get(
      `${routes.credentials.get}?seed=0&count=100`,
      getAxiosHeaders()
    );
    if (res?.data?.code === 0) {
      if (txnInfo) {
        // update verifications in form data based on which of them exist in wallet
        const formattedTxnInfo = structuredClone(txnInfo);
        if (formattedTxnInfo && formattedTxnInfo?.verificationList) {
          Object.keys(formattedTxnInfo.verificationList ?? {}).forEach((key) => {
            const foundCred = res?.data?.data?.find((r: any) => r?.credentialType === key);
            if (foundCred) {
              formattedTxnInfo.verificationList[key] = {
                ...(formattedTxnInfo.verificationList[key] ?? {}),
                existsInWallet: true,
                credentialAttributes: foundCred?.credentialAttributes
              };
            }
          });
          store.dispatch(
            updateFormData({
              txnInfo: formattedTxnInfo
            })
          );
        }
      }
    }
    return res?.data;
  } catch (e) {
    console.info(e);
  }
};

export const markAsSkip = async (reqData: {
  orgId: string,
  txnId: string,
  verificationNameList: string[]
}) => {
  try {
    const res = await axiosInstances.verificationV1?.post(
      routes.transactions.markAsSkip,
      reqData,
      getAxiosHeaders()
    );
    return res?.data;
  } catch (error) {
    console.info(error);
  }
};

export const getCandidateTxns = async (seed = 0, count = 10, states?: string[]) => {
  const statesQueryParam = states ? `&states=${states?.join(',')}` : '';
  const queryParams = `seed=${seed}&count=${count}${statesQueryParam}`;
  try {
    const res = await axiosInstances.verificationV1?.get(
      `${routes.transactions.candidateTxn}?${queryParams}`,
      getAxiosHeaders()
    );
    return res?.data;
  } catch (e) {
    console.info(e);
  }
};

export const getPublicEkycForms = async (seed = 0, count = 10, orgId?: string) => {
  const orgIdQParam = orgId ? `&orgId=${orgId}` : '';
  const queryParams = `seed=${seed}&count=${count}${orgIdQParam}`;
  try {
    const res = await axiosInstances.verificationV1?.get(
      `${routes.ekycForms.public}?${queryParams}`,
      getAxiosHeaders()
    );
    return res?.data;
  } catch (e) {
    console.info(e);
  }
};

export const receiveCredInWallet = async (verificationName: string, reqData: unknown) => {
  try {
    const res = await axiosInstances.webWallet?.post(
      routes.credentials.receive(verificationName),
      reqData,
      getAxiosHeaders()
    );
    return res?.data;
  } catch (error) {
    console.info(error);
  }
};

export const resolveBGVInsuff = async (reqData: BGVInsuffPayload) => {
  try {
    const res = await axiosInstances.verificationV1?.post(`${routes.bgv.insufficiency}`, reqData, {
      headers: { Authorization: `Bearer ${getAccessToken()}` }
    });
    return res?.data;
  } catch (e) {
    console.info(e);
  }
};

export const getDataByPincode = async (pincode: string) => {
  try {
    const res = await axiosInstances.utility?.get(`${routes.pincode.base}/${pincode}`);
    return res?.data;
  } catch (e) {
    console.info(e);
  }
};

export const updateIdentification = async (reqData: {
  identificationValue: string,
  orgId: string,
  txnId: string
}) => {
  try {
    const res = await axiosInstances.verificationV1?.post(
      `${routes.transactions.updateIdentification}`,
      reqData,
      {
        headers: { Authorization: `Bearer ${getAccessToken()}` }
      }
    );
    return res?.data;
  } catch (e) {
    console.info(e);
  }
};
export const nameChangeApiCall = async (reqData: {
  orgId: string,
  txnId: string,
  verificationName: string,
  documentList: { label: string, url: string }[]
}) => {
  try {
    const res = await axiosInstances.verificationV1?.post(
      routes.transactions.nameChange,
      reqData,
      getAxiosHeaders()
    );
    return res?.data;
  } catch (error) {
    console.info(error);
  }
};

export const exportData = async (reqData: { email: string }) => {
  try {
    const res = await axiosInstances.verificationV1?.post(
      routes.transactions.exportData,
      reqData,
      getAxiosHeaders()
    );
    return res?.data;
  } catch (error) {
    console.info(error);
    return false;
  }
};

export const deleteAccount = async () => {
  try {
    const res = await axiosInstances.webWallet?.patch(
      routes.profile.deleteAccount,
      {},
      getAxiosHeaders()
    );
    return res?.data;
  } catch (error) {
    console.info(error);
  }
};

export const deleteVerificationFromTxn = async (subType: string, orgId: string, txnId: string) => {
  try {
    const res = await axiosInstances.verificationV2?.delete(`${routes.bgv.base}`, {
      data: { subType, orgId, txnId },
      ...getAxiosHeaders()
    });
    return res?.data;
  } catch (e) {
    console.info(e);
    return false;
  }
};

export const detectDocumentIssues = async (reqData: { file?: string, fileUrl?: string }) => {
  if (reqData.file && typeof reqData.file === 'string') {
    reqData.file = reqData.file.replace(/^"|"$/g, '');
  }
  try {
    const res = await axiosInstances.fx?.post(routes.fx.document_issues, reqData, {
      headers: {
        'x-api-key': import.meta.env.VITE_FX_API_KEY,
        'Content-Type': 'application/json'
      }
    });
    return res?.data;
  } catch (error) {
    console.info(error);
  }
};

export const preVerifyDigilocker = async (payload: { redirectUrl: string, docTypes: string[] }) => {
  try {
    const res = await axiosInstances.verificationV1?.post(
      routes.digilocker.preVerify,
      payload,
      getAxiosHeaders()
    );
    return res?.data?.data;
  } catch (error) {
    console.info(error);
  }
};

export const getEvalConfigs = async (providers: string[]) => {
  try {
    const res = await axiosInstances.verificationV1?.get(
      routes.transactions.evalConfigs(providers.join(',')),
      getAxiosHeaders()
    );
    return res?.data;
  } catch (e) {
    console.info(e);
  }
};

export const getDigilockerVerificationDetails = async (body: object) => {
  try {
    const res = await axiosInstances.webWallet?.put(
      routes.digilocker.verificationDetails,
      body,
      getAxiosHeaders()
    );
    return res?.data;
  } catch (error) {
    console.info(error);
  }
};
